import useGetInternalWidgetConfig from '@hooks/api/queries/widgets/useGetInternalWidgetConfig';
import storeConnector from '@src/store/storeConnector';
import { DataPointWithValue } from '@src/types/DataPoint';
import { SiteMeta } from '@src/types/SiteMeta';
import { WidgetTypeDeviceList } from '@src/types/Widget';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';

type UnitContextType = {
  siteMetaUnits: SiteMeta['Units'];
  unitWidgetConfig: WidgetTypeDeviceList | undefined;
  getDataPointValue: (
    engineeringId: string,
    pointAlias: string,
    dataPointsWithValue: DataPointWithValue[],
  ) => string;
};

const UnitContext = createContext<UnitContextType | undefined>(undefined);

type UnitProviderProps = {
  children: ReactNode;
  siteMeta: SiteMeta;
};

export function UnitProvider({ children, siteMeta }: UnitProviderProps) {
  const siteMetaUnits = siteMeta.Units;
  const { data: unitWidgetConfig } =
    useGetInternalWidgetConfig<WidgetTypeDeviceList>(
      'sagebrushB59_sld_unit',
      {},
      { enabled: true },
    );

  const getDataPointValue = useCallback(
    (
      engineeringId: string,
      pointName: string,
      dataPointsWithValue: DataPointWithValue[],
    ): string => {
      const dataPoint = dataPointsWithValue.find(
        (dp) => dp.controllerId === engineeringId && dp.pointName === pointName,
      );
      return dataPoint?.value?.toString() || '--';
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      siteMetaUnits,
      unitWidgetConfig,
      getDataPointValue,
    }),
    [siteMetaUnits, unitWidgetConfig, getDataPointValue],
  );

  return (
    <UnitContext.Provider value={contextValue}>{children}</UnitContext.Provider>
  );
}

export default storeConnector(UnitProvider, {
  config: ['siteMeta'],
});

export function useUnitContext() {
  const context = useContext(UnitContext);
  if (context === undefined) {
    throw new Error('useUnitContext must be used within a UnitProvider');
  }
  return context;
}
